// ─────────────────────────────────────────────────────────────────────────────
// admin/src/pages/BlogsPage.jsx
//
// Admin page for managing blog posts stored in Firestore `blogs` collection.
//
// Firestore document shape:
//   {
//     tag:       string   — category label shown as badge, e.g. "GSAP"
//     color:     string   — hex accent for the tag badge, e.g. "#1677ff"
//     title:     string   — post headline
//     excerpt:   string   — short teaser paragraph (shown on cards)
//     content:   string   — full post body (markdown-lite: ##, -, ** supported)
//     date:      string   — display string e.g. "MAY 2025"
//     readTime:  string   — display string e.g. "6 MIN"
//     cover:     string   — full image URL
//     order:     number   — integer for sort order
//     published: boolean  — false = draft, true = live on portfolio
//   }
//
// HOW TO PLUG IN:
//   1. Copy this file into admin/src/pages/BlogsPage.jsx
//   2. In your firestore.js, make sure blogsService is exported (already done per your file)
//   3. Add the route + sidebar menu item as you do for other pages
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Button, Space, Typography, Modal, Form,
  Input, InputNumber, Switch, Select, Tag, Tooltip,
  Popconfirm, message, Card, Row, Col,
  Badge, Divider, Alert, Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined,
  FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined,
  GlobalOutlined, CopyOutlined,
} from '@ant-design/icons';

// ── Import blogsService from your existing firestore.js ──────────────────────
// Adjust path to match your admin project structure
import { blogsService } from '../firebase/services';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ── Preset tag colors ─────────────────────────────────────────────────────────
const PRESET_COLORS = [
  { label: 'Blue',   value: '#1677ff' },
  { label: 'Green',  value: '#16a34a' },
  { label: 'Purple', value: '#9333ea' },
  { label: 'Orange', value: '#f59e0b' },
  { label: 'Red',    value: '#ef4444' },
  { label: 'Cyan',   value: '#0891b2' },
  { label: 'Pink',   value: '#ec4899' },
  { label: 'Indigo', value: '#6366f1' },
];

// ── Suggested category tags ───────────────────────────────────────────────────
const SUGGESTED_TAGS = [
  'GSAP', 'React', 'Design', 'JavaScript', 'CSS',
  'Animation', 'Career', 'Productivity', 'AI', 'Backend',
  'Frontend', 'Tutorial', 'Case Study', 'Opinion',
];

// ── Empty post template ───────────────────────────────────────────────────────
const EMPTY_POST = {
  tag:       '',
  color:     '#1677ff',
  title:     '',
  excerpt:   '',
  content:   '',
  date:      '',
  readTime:  '',
  cover:     '',
  order:     0,
  published: false,
};

// ─────────────────────────────────────────────────────────────────────────────
export default function BlogsPage() {
  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [previewPost, setPreviewPost] = useState(null);
  const [form]                        = Form.useForm();

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await blogsService.getAll();
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setPosts(data);
    } catch (e) {
      message.error('Failed to load posts: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // ── Open new ───────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditingPost(null);
    form.setFieldsValue({ ...EMPTY_POST, order: posts.length + 1 });
    setModalOpen(true);
  };

  // ── Open edit ──────────────────────────────────────────────────────────────
  const openEdit = (post) => {
    setEditingPost(post);
    form.setFieldsValue({ ...post });
    setModalOpen(true);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch { return; }
    setSaving(true);
    try {
      if (editingPost) {
        await blogsService.update(editingPost.id, values);
        message.success('Post updated');
      } else {
        await blogsService.add(values);
        message.success('Post created');
      }
      setModalOpen(false);
      form.resetFields();
      await fetchPosts();
    } catch (e) {
      message.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle published ───────────────────────────────────────────────────────
  const togglePublished = async (post) => {
    try {
      await blogsService.update(post.id, { published: !post.published });
      message.success(post.published ? 'Post unpublished' : 'Post published live');
      await fetchPosts();
    } catch (e) {
      message.error('Update failed: ' + e.message);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await blogsService.remove(id);
      message.success('Post deleted');
      await fetchPosts();
    } catch (e) {
      message.error('Delete failed: ' + e.message);
    }
  };

  // ── Duplicate ──────────────────────────────────────────────────────────────
  const handleDuplicate = async (post) => {
    try {
      const { id, ...data } = post;
      await blogsService.add({ ...data, title: `${data.title} (copy)`, published: false, order: posts.length + 1 });
      message.success('Duplicated as draft');
      await fetchPosts();
    } catch (e) {
      message.error('Duplicate failed: ' + e.message);
    }
  };

  // ── Stats ──────────────────────────────────────────────────────────────────
  const publishedCount = posts.filter(p => p.published).length;
  const draftCount     = posts.length - publishedCount;

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: '#', dataIndex: 'order', key: 'order', width: 52, align: 'center',
      render: v => <Text type="secondary" style={{ fontSize: 13, fontWeight: 600 }}>{v ?? '—'}</Text>,
    },
    {
      title: 'Cover', dataIndex: 'cover', key: 'cover', width: 80,
      render: url => url ? (
        <div style={{ width: 64, height: 44, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <img src={url} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; }} />
        </div>
      ) : (
        <div style={{
          width: 64, height: 44, borderRadius: 8, background: '#f8fafc',
          border: '1px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FileTextOutlined style={{ color: '#94a3b8', fontSize: 16 }} />
        </div>
      ),
    },
    {
      title: 'Title', dataIndex: 'title', key: 'title',
      render: (title, record) => (
        <div>
          <Text strong style={{ fontSize: 14, display: 'block', lineHeight: 1.35 }}>
            {title || <Text type="secondary">Untitled</Text>}
          </Text>
          {record.excerpt && (
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
              {record.excerpt.length > 80 ? record.excerpt.slice(0, 80) + '…' : record.excerpt}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Tag', dataIndex: 'tag', key: 'tag', width: 120,
      render: (tag, record) => tag ? (
        <Tag style={{
          background: (record.color || '#1677ff') + '18',
          color: record.color || '#1677ff',
          border: `1px solid ${(record.color || '#1677ff')}40`,
          borderRadius: 20, fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
        }}>
          {tag}
        </Tag>
      ) : '—',
    },
    {
      title: 'Date', dataIndex: 'date', key: 'date', width: 110,
      render: v => <Text style={{ fontSize: 13 }}>{v || '—'}</Text>,
    },
    {
      title: 'Read', dataIndex: 'readTime', key: 'readTime', width: 90,
      render: v => <Text style={{ fontSize: 13 }}>{v || '—'}</Text>,
    },
    {
      title: 'Status', dataIndex: 'published', key: 'published', width: 110, align: 'center',
      render: (published, record) => (
        <Tooltip title={published ? 'Click to unpublish' : 'Click to publish live'}>
          <Tag onClick={() => togglePublished(record)} style={{
            cursor: 'pointer', borderRadius: 20, fontWeight: 600, fontSize: 11,
            background: published ? '#f0fdf4' : '#f8fafc',
            color:      published ? '#16a34a' : '#64748b',
            border:     published ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
            padding: '3px 12px',
          }}>
            {published ? '● Live' : '○ Draft'}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Actions', key: 'actions', width: 160, align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Preview">
            <Button type="text" icon={<EyeOutlined />} size="small"
              onClick={() => setPreviewPost(record)} style={{ color: '#6366f1' }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} size="small"
              onClick={() => openEdit(record)} style={{ color: '#1677ff' }} />
          </Tooltip>
          <Tooltip title="Duplicate as draft">
            <Button type="text" icon={<CopyOutlined />} size="small"
              onClick={() => handleDuplicate(record)} style={{ color: '#f59e0b' }} />
          </Tooltip>
          <Popconfirm
            title="Delete this post?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button type="text" icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0 0 48px' }}>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 28, flexWrap: 'wrap', gap: 16,
      }}>
        <div>
          <Title level={3} style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>Blog Posts</Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Manage your portfolio blog — saved directly to Firestore
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchPosts} loading={loading} style={{ borderRadius: 8 }}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}
            style={{ borderRadius: 8, fontWeight: 700, background: '#111', borderColor: '#111' }}>
            New Post
          </Button>
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        {[
          { title: 'Total Posts',  value: posts.length,   icon: <FileTextOutlined  style={{ fontSize: 20, color: '#6366f1' }} />, bg: '#faf5ff', border: '#e9d5ff' },
          { title: 'Published',    value: publishedCount, icon: <CheckCircleOutlined style={{ fontSize: 20, color: '#16a34a' }} />, bg: '#f0fdf4', border: '#bbf7d0' },
          { title: 'Drafts',       value: draftCount,     icon: <ClockCircleOutlined style={{ fontSize: 20, color: '#f59e0b' }} />, bg: '#fffbeb', border: '#fde68a' },
          { title: 'Live on Site', value: publishedCount, icon: <GlobalOutlined     style={{ fontSize: 20, color: '#1677ff' }} />, bg: '#eff6ff', border: '#bfdbfe' },
        ].map((s, i) => (
          <Col key={i} xs={12} sm={6}>
            <Card style={{ borderRadius: 12, border: `1px solid ${s.border}`, background: s.bg }}
              bodyStyle={{ padding: '20px 20px 16px' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                <div>
                  <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {s.title}
                  </Text>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginTop: 4 }}>
                    {s.value}
                  </div>
                </div>
                {s.icon}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table */}
      <Card style={{ borderRadius: 16, border: '1px solid #e8edf5' }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={posts} columns={columns} rowKey="id" loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false, showTotal: t => `${t} posts`,
            style: { padding: '16px 24px', margin: 0 } }}
          scroll={{ x: 900 }}
          locale={{ emptyText: (
            <Empty description={<Text type="secondary">No posts yet — click "New Post" to add one.</Text>}
              style={{ padding: '40px 0' }} />
          )}}
          style={{ borderRadius: 16 }}
        />
      </Card>

      {/* ── CREATE / EDIT MODAL ── */}
      <Modal
        open={modalOpen}
        title={<Space><FileTextOutlined /><Text strong style={{ fontSize: 16 }}>
          {editingPost ? 'Edit Post' : 'New Blog Post'}
        </Text></Space>}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={handleSave}
        okText={saving ? 'Saving…' : (editingPost ? 'Save Changes' : 'Create Post')}
        okButtonProps={{ loading: saving, style: { background: '#111', borderColor: '#111', fontWeight: 700, borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={820}
        styles={{ body: { paddingTop: 8, maxHeight: '72vh', overflowY: 'auto' } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={EMPTY_POST}>

          {/* Title */}
          <Form.Item name="title" label={<Text strong>Post Title</Text>}
            rules={[{ required: true, message: 'Title is required' }]}>
            <Input size="large" placeholder="Mastering GSAP ScrollTrigger in 2025" style={{ borderRadius: 8 }} />
          </Form.Item>

          {/* Tag + Color + Order */}
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item name="tag" label={<Text strong>Category Tag</Text>}
                rules={[{ required: true, message: 'Tag is required' }]}>
                <Select showSearch allowClear placeholder="Select or type a tag"
                  size="large" style={{ borderRadius: 8 }}
                  dropdownRender={menu => (
                    <>
                      {menu}
                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ padding: '4px 12px 8px' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Type your own tag name above</Text>
                      </div>
                    </>
                  )}
                  filterOption={(input, option) =>
                    option?.value?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {SUGGESTED_TAGS.map(t => <Option key={t} value={t}>{t}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="color" label={<Text strong>Tag Color</Text>}>
                <Select size="large" style={{ borderRadius: 8 }}>
                  {PRESET_COLORS.map(c => (
                    <Option key={c.value} value={c.value}>
                      <Space>
                        <span style={{
                          display: 'inline-block', width: 14, height: 14,
                          borderRadius: '50%', background: c.value, border: '1px solid rgba(0,0,0,0.1)',
                        }} />
                        {c.label}
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="order" label={<Text strong>Sort Order</Text>}>
                <InputNumber size="large" min={0} style={{ width: '100%', borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Excerpt */}
          <Form.Item name="excerpt" label={<Text strong>Excerpt</Text>}
            rules={[{ required: true, message: 'Excerpt is required' }]}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Short teaser shown on blog cards. Keep under 180 characters.</Text>}>
            <TextArea rows={3} showCount maxLength={300}
              placeholder="A short description that draws readers in…"
              style={{ borderRadius: 8, resize: 'none' }} />
          </Form.Item>

          {/* Content */}
          <Form.Item
            name="content"
            label={<Space>
              <Text strong>Post Content</Text>
              <Tag style={{ borderRadius: 20, fontSize: 11 }}>Markdown-lite</Tag>
            </Space>}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>
              Supports: <code>## Heading</code> · <code>- list item</code> · <code>**bold**</code>. Each paragraph = one line.
            </Text>}
          >
            <TextArea rows={14}
              placeholder={'## Introduction\n\nYour post content here...\n\n## Section Two\n\n- Point one\n- Point two\n\nAnother paragraph.'}
              style={{ borderRadius: 8, fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }} />
          </Form.Item>

          {/* Cover */}
          <Form.Item name="cover" label={<Text strong>Cover Image URL</Text>}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>Direct image URL (Unsplash, Supabase Storage, etc.)</Text>}>
            <Input size="large" placeholder="https://images.unsplash.com/photo-…"
              style={{ borderRadius: 8 }} addonBefore="🖼" />
          </Form.Item>

          {/* Cover preview */}
          <Form.Item noStyle shouldUpdate={(p, c) => p.cover !== c.cover}>
            {({ getFieldValue }) => {
              const url = getFieldValue('cover');
              return url ? (
                <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden', height: 140, border: '1px solid #e2e8f0' }}>
                  <img src={url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = ''; }} />
                </div>
              ) : null;
            }}
          </Form.Item>

          {/* Date + ReadTime */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="date" label={<Text strong>Display Date</Text>}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>e.g. "MAY 2025" or "June 10, 2025"</Text>}>
                <Input size="large" placeholder="MAY 2025" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="readTime" label={<Text strong>Read Time</Text>}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>e.g. "6 MIN" or "8 min read"</Text>}>
                <Input size="large" placeholder="6 MIN" style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>

          {/* Published */}
          <Divider />
          <Form.Item name="published" valuePropName="checked"
            label={<Space>
              <Text strong>Published</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>— Toggle on to make this post live on your portfolio</Text>
            </Space>}>
            <Switch checkedChildren="Live" unCheckedChildren="Draft" />
          </Form.Item>

          <Alert type="info" showIcon message="Firestore sync"
            description="Saving updates your Firestore `blogs` collection immediately. Published posts appear live on the portfolio."
            style={{ borderRadius: 8, marginTop: 8 }} />
        </Form>
      </Modal>

      {/* ── PREVIEW MODAL ── */}
      <Modal
        open={!!previewPost}
        title={<Space><EyeOutlined /><Text strong>Post Preview</Text></Space>}
        onCancel={() => setPreviewPost(null)}
        footer={
          <Space>
            <Button onClick={() => setPreviewPost(null)} style={{ borderRadius: 8 }}>Close</Button>
            <Button type="primary" icon={<EditOutlined />}
              onClick={() => { openEdit(previewPost); setPreviewPost(null); }}
              style={{ background: '#111', borderColor: '#111', borderRadius: 8, fontWeight: 700 }}>
              Edit Post
            </Button>
          </Space>
        }
        width={720}
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', padding: '24px 32px' } }}
        destroyOnClose
      >
        {previewPost && (
          <div>
            {previewPost.cover && (
              <div style={{ borderRadius: 12, overflow: 'hidden', height: 200, marginBottom: 24, border: '1px solid #e2e8f0' }}>
                <img src={previewPost.cover} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
              {previewPost.tag && (
                <Tag style={{
                  background: (previewPost.color || '#1677ff') + '18',
                  color: previewPost.color || '#1677ff',
                  border: `1px solid ${(previewPost.color || '#1677ff')}40`,
                  borderRadius: 20, fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
                }}>{previewPost.tag}</Tag>
              )}
              <Badge status={previewPost.published ? 'success' : 'default'}
                text={<Text style={{ fontSize: 13 }}>{previewPost.published ? 'Live' : 'Draft'}</Text>} />
              {previewPost.date     && <Text type="secondary" style={{ fontSize: 13 }}>📅 {previewPost.date}</Text>}
              {previewPost.readTime && <Text type="secondary" style={{ fontSize: 13 }}>⏱ {previewPost.readTime}</Text>}
            </Space>

            <Title level={3} style={{ margin: '0 0 12px', color: '#0f172a', lineHeight: 1.25 }}>
              {previewPost.title}
            </Title>

            {previewPost.excerpt && (
              <div style={{ background: '#f8faff', borderLeft: '4px solid #1677ff',
                borderRadius: '0 8px 8px 0', padding: '14px 18px', marginBottom: 24 }}>
                <Text style={{ fontSize: 15, color: '#374151', fontStyle: 'italic' }}>{previewPost.excerpt}</Text>
              </div>
            )}

            <Divider />

            {previewPost.content ? (
              <div>
                {previewPost.content.split('\n').map((line, i) => {
                  const t = line.trim();
                  if (!t) return <div key={i} style={{ height: 10 }} />;
                  if (t.startsWith('## ')) return (
                    <Title key={i} level={4} style={{ marginTop: 24, marginBottom: 10, color: '#0f172a' }}>
                      {t.replace('## ', '')}
                    </Title>
                  );
                  if (t.startsWith('- ')) return (
                    <li key={i} style={{ marginLeft: 20, marginBottom: 6, fontSize: 15, color: '#374151' }}>
                      {t.replace('- ', '')}
                    </li>
                  );
                  return <Paragraph key={i} style={{ marginBottom: 12, fontSize: 15, lineHeight: 1.8, color: '#374151' }}>{t}</Paragraph>;
                })}
              </div>
            ) : (
              <Text type="secondary">No content written yet.</Text>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
